import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Box,
  Typography,
  TableSortLabel,
  Skeleton,
} from '@mui/material';

/**
 * Reusable Data Table Component
 * Consistent table styling across all list pages
 * 
 * @param {array} columns - Column definitions [{ id, label, align, width, sortable, render }]
 * @param {array} data - Array of data objects
 * @param {boolean} loading - Whether data is loading
 * @param {boolean} selectable - Whether rows are selectable
 * @param {array} selected - Array of selected row IDs
 * @param {function} onSelectAll - Callback when select all is clicked
 * @param {function} onSelectRow - Callback when a row is selected
 * @param {function} onRowClick - Callback when a row is clicked
 * @param {string} idField - Field name for row ID (default: '_id')
 * @param {string} sortBy - Current sort field
 * @param {string} sortOrder - Current sort order ('asc' or 'desc')
 * @param {function} onSort - Callback when sort changes
 * @param {React.ReactNode} emptyState - Custom empty state component
 * @param {boolean} stickyHeader - Whether header should be sticky
 * @param {number} maxHeight - Maximum table height for scrolling
 * @param {object} sx - Additional styles
 */
const DataTable = ({
  columns = [],
  data = [],
  loading = false,
  selectable = false,
  selected = [],
  onSelectAll,
  onSelectRow,
  onRowClick,
  idField = '_id',
  sortBy,
  sortOrder = 'asc',
  onSort,
  emptyState,
  stickyHeader = false,
  maxHeight,
  sx = {},
  rowHoverEffect = true,
  dense = false,
}) => {
  const isSelected = (id) => selected.includes(id);
  const allSelected = data.length > 0 && selected.length === data.length;
  const someSelected = selected.length > 0 && selected.length < data.length;

  const handleSelectAll = (event) => {
    if (onSelectAll) {
      onSelectAll(event);
    }
  };

  const handleSelectRow = (event, id) => {
    event.stopPropagation();
    if (onSelectRow) {
      onSelectRow(event, id);
    }
  };

  const handleSort = (columnId) => {
    if (onSort) {
      const newOrder = sortBy === columnId && sortOrder === 'asc' ? 'desc' : 'asc';
      onSort(columnId, newOrder);
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 'none', 
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          ...sx 
        }}
      >
        <Table size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              {selectable && <TableCell padding="checkbox" />}
              {columns.map((column) => (
                <TableCell key={column.id} width={column.width}>
                  <Skeleton width="60%" />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                {selectable && (
                  <TableCell padding="checkbox">
                    <Skeleton variant="rectangular" width={18} height={18} />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.id}>
                    <Skeleton />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Empty state
  if (data.length === 0) {
    return emptyState || (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          color: 'text.secondary',
        }}
      >
        <Typography variant="h6" gutterBottom>
          No data found
        </Typography>
        <Typography variant="body2">
          Try adjusting your search or filters
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        maxHeight: maxHeight,
        ...sx,
      }}
    >
      <Table 
        stickyHeader={stickyHeader} 
        size={dense ? 'small' : 'medium'}
      >
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell 
                padding="checkbox"
                sx={{ 
                  bgcolor: '#f8f9fa',
                  borderBottom: '2px solid #e0e0e0',
                }}
              >
                <Checkbox
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={handleSelectAll}
                  size="small"
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                width={column.width}
                sx={{
                  bgcolor: '#f8f9fa',
                  borderBottom: '2px solid #e0e0e0',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: '#333',
                  whiteSpace: 'nowrap',
                }}
              >
                {column.sortable && onSort ? (
                  <TableSortLabel
                    active={sortBy === column.id}
                    direction={sortBy === column.id ? sortOrder : 'asc'}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => {
            const rowId = row[idField];
            const isRowSelected = isSelected(rowId);

            return (
              <TableRow
                key={rowId}
                selected={isRowSelected}
                onClick={() => onRowClick && onRowClick(row)}
                hover={rowHoverEffect}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  '&:hover': rowHoverEffect ? {
                    bgcolor: '#f8f9fa',
                  } : {},
                  '&.Mui-selected': {
                    bgcolor: 'rgba(135, 90, 123, 0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(135, 90, 123, 0.12)',
                    },
                  },
                }}
              >
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isRowSelected}
                      onClick={(e) => handleSelectRow(e, rowId)}
                      size="small"
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{
                      fontSize: '13px',
                      color: '#333',
                      py: dense ? 1 : 1.5,
                    }}
                  >
                    {column.render 
                      ? column.render(row[column.id], row) 
                      : row[column.id]
                    }
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DataTable;
